import { AddCircleOutline } from '@mui/icons-material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { AlertColor, Box, Breadcrumbs, Button, Grid, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import strings from 'common/Translation/Translate';
import Form, { FormDataModel } from 'components/Form';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import dayjs from 'dayjs';
import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  createDropdownData,
  createLayoutDropdownData,
  fetchErrorFields,
  getLineItemDrawerWidth,
  removeErrorFieldsFromValues,
  buildVenuesForEventType,
  VenueEventCapacity,
} from 'utils/validators/HelperFunctions';
import { fetchFromStorage } from 'utils/storage';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import DrawerMenu from 'components/DrawerMenu';
import { NewBookingAddon } from 'screens/Authenticated/NewBooking/NewBookingAddOn';
import VenueDetailsPageSummary from 'screens/Authenticated/NewBooking/VenueDetailsPageSummary';
import { extractCostingErrorMessage, VENUE_DETAILS_COSTING_DEBOUNCE_MS } from 'screens/Authenticated/NewBooking/venueDetailsCostingUtils';
import { lineItemsForm, lineItemsTaxForm } from 'screens/Authenticated/NewBooking/MultipleLineItemsForm';
import PricingStrategySection, {
  PricingStrategyValues,
  buildHourlyRentalBreakdown,
  calcHourlyTotalRental,
  getEffectivePricePerHour,
} from 'components/PricingStrategySection';
import { URLS } from 'utils/constants/urls';
import axiosInstance from 'utils/axios';
import { NewBookingSendEmailFormComponent } from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/SendEmailFormComponent';
import { User } from 'utils/redux';
import NewBookingSendEmailComponent from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent';
import {
  appendEmailAttachmentOptions,
  getEmailAttachmentVisibility,
} from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/emailAttachmentOptions';
import { status } from 'utils/constants/data';
import { determinePartOfDay } from 'utils/validators';
import { bookingDetailToolbarButtonSx } from '../../bookingDetailToolbarSx';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';
import { buildAddLineItemCostingRequest } from './addLineItemCostingUtils';

type AddLineItemProps = {
  eventType: any;
  id: any;
  onSuccess: () => void;
  bookingEmail: string;
  bookingId: string;
  dates: { startDate: any; endDate: any };
  pageHeader?: {
    customerName?: string;
    eventTypeLabel?: string;
  };
  isContractTemplatePresent?: boolean;
  isProposalTemplatePresent?: boolean;
  isPiTemplatePresent?: boolean;
  isEnquiryTemplatePresent?: boolean;
};

const AddLineItem = ({
  onSuccess,
  bookingEmail,
  eventType,
  dates,
  bookingId,
  pageHeader,
  isContractTemplatePresent = false,
  isProposalTemplatePresent = false,
  isPiTemplatePresent = false,
  isEnquiryTemplatePresent = false,
}: AddLineItemProps) => {
  const theme = useTheme();
  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  let lineItemFormRef: RefObject<Form | null | undefined> = useRef();
  let lineItemTaxFormRef: RefObject<Form | null | undefined> = useRef();
  let sendEmailFormRef: RefObject<Form | null | undefined> = useRef();
  const loggedInUserDetails = useSelector(User);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [layoutList, setLayoutList] = useState<any>([]);
  const [taxGroupList, setTaxGroupList] = useState<any>([]);
  const [serviceChargeList, setServiceChargeList] = useState<any>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [addonsList, setAddonsList] = useState<any>([]);
  const [venueList, setVenueList] = useState<any>([]);
  const [baseVenueList, setBaseVenueList] = useState<any>([]);
  const [venueEventCapacities, setVenueEventCapacities] = useState<VenueEventCapacity[]>([]);
  const [venueMaxCapacity, setVenueMaxCapacity] = useState<number | null>(null);
  const [partOfDays, setPartOfDays] = useState<any>([]);
  const [statusList, setStatusList] = useState<any>([]);
  const [pricePackageList, setPricePackageList] = useState<any>([]);
  const [hourlyPricing, setHourlyPricing] = useState<boolean>(false);
  const [lineItemsPageResponse, setLineItemsPageResponse] = useState<any>({
    ...dates,
    partOfDay: '',
    partOfDayUserOverride: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bookedAddOns, setBookedAddOns] = useState<any>([]);
  const [sendEmailHasError, setSendEmailHasError] = useState<boolean>(false);
  const [sendEmailPageResponse, setSendEmailPageResponse] = useState<any>({});
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<any>([]);
  const [deletedImage] = useState<any>([]);
  const [defaultPricePerPerson, setDefaultPricePerPerson] = useState<any>('');
  const [defaultMinimumRevenue, setDefaultMinimumRevenue] = useState<any>('');
  const [defaultPricePerHour, setDefaultPricePerHour] = useState<any>('');
  const [defaultHallRental, setDefaultHallRental] = useState<any>(undefined);
  const [isOdc, setIsOdc] = useState<boolean>(false);
  const [eventSubtypeOptions, setEventSubtypeOptions] = useState<any[]>([]);
  const [pastBookingWindowDays, setPastBookingWindowDays] = useState<number | null>(null);
  const [pricing, setPricing] = useState<PricingStrategyValues>({
    overridePricePerPerson: false,
    pricePerPerson: null,
    overrideMinimumRevenue: false,
    minimumRevenue: null,
    overrideHallRental: false,
    hallRental: null,
    overridePricePerHour: false,
    pricePerHour: null,
  });
  const hourlyPricingBreakdown = useMemo(() => {
    if (!hourlyPricing) return undefined;
    const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
    return buildHourlyRentalBreakdown(
      lineItemsPageResponse?.startTime,
      lineItemsPageResponse?.endTime,
      rate,
    );
  }, [
    hourlyPricing,
    pricing.overridePricePerHour,
    pricing.pricePerHour,
    defaultPricePerHour,
    lineItemsPageResponse?.startTime,
    lineItemsPageResponse?.endTime,
    lineItemsPageResponse?.pricePerHour,
  ]);

  const hourlyTotalRental = useMemo(() => {
    if (!hourlyPricing) return null;
    const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
    const total = calcHourlyTotalRental(
      lineItemsPageResponse?.startTime,
      lineItemsPageResponse?.endTime,
      rate,
    );
    return total === '' ? null : total;
  }, [
    hourlyPricing,
    pricing.overridePricePerHour,
    pricing.pricePerHour,
    defaultPricePerHour,
    lineItemsPageResponse?.startTime,
    lineItemsPageResponse?.endTime,
    lineItemsPageResponse?.pricePerHour,
  ]);

  const [costingPageResponse, setCostingPageResponse] = useState<Record<string, any>>({});
  const [costingLoading, setCostingLoading] = useState(false);
  const [costingError, setCostingError] = useState<string | null>(null);
  const costingDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const costingRequestSeqRef = useRef(0);
  const costingStateRef = useRef({
    formData: lineItemsPageResponse,
    eventType,
    customerName: pageHeader?.customerName,
    pricing,
    bookedAddOns,
    hourlyPricing,
    pricePackages: pricePackageList,
  });

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const [outsideMessage, setOutsideMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const closeOutsideNotification = (value: boolean) => {
    setOutsideMessage({ ...outsideMessage, display: value });
  };

  const getFileDetails = (value: string) => {
    setAttachment(value);
  };

  const masterLoadedRef = useRef(false);
  /** Sync guard so a second Save click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const lineItemIdempotencyKeyRef = useRef('');
  const pendingLineItemRequestRef = useRef<Record<string, any> | null>(null);
  const lineItemCreatedRef = useRef(false);
  const [pendingLineItemStatus, setPendingLineItemStatus] = useState<string>('');
  const emailAttachmentVisibility = getEmailAttachmentVisibility({
    isContractTemplatePresent,
    isProposalTemplatePresent,
    isPiTemplatePresent,
    isEnquiryTemplatePresent,
    isEnquiryStatus: pendingLineItemStatus === 'ENQUIRY',
  });

  const refreshAddLineItemCosting = useCallback(async () => {
    const {
      formData,
      eventType: evt,
      customerName,
      pricing: pricingValues,
      bookedAddOns: addOns,
      hourlyPricing: isHourly,
      pricePackages: packages,
    } = costingStateRef.current;

    if (!formData?.venue) {
      setCostingPageResponse({});
      setCostingError(null);
      return;
    }
    const requestBody = buildAddLineItemCostingRequest({
      formData,
      eventType: evt,
      customerName,
      pricing: pricingValues,
      bookedAddOns: addOns,
      hourlyPricing: isHourly,
      pricePackages: packages ?? [],
    });
    if (!requestBody) {
      setCostingPageResponse({});
      setCostingError(null);
      return;
    }

    const requestSeq = ++costingRequestSeqRef.current;
    setCostingLoading(true);
    try {
      const { status: httpStatus, data: responseData } = await axiosInstance.post(
        URLS.calculateBill,
        requestBody,
      );
      if (requestSeq !== costingRequestSeqRef.current) return;
      if (httpStatus === 200 || httpStatus === 201) {
        setCostingPageResponse(responseData ?? {});
        setCostingError(null);
      }
    } catch (error: any) {
      if (requestSeq !== costingRequestSeqRef.current) return;
      setCostingPageResponse({});
      const message = extractCostingErrorMessage(error, strings.new_booking_event_summary_error);
      setCostingError(message);
      setMessage({
        display: true,
        severity: 'error',
        message,
      });
    } finally {
      if (requestSeq === costingRequestSeqRef.current) {
        setCostingLoading(false);
      }
    }
  }, []);

  const scheduleAddLineItemCostingRefresh = useCallback(() => {
    if (costingDebounceTimerRef.current) {
      clearTimeout(costingDebounceTimerRef.current);
    }
    costingDebounceTimerRef.current = setTimeout(() => {
      costingDebounceTimerRef.current = null;
      void refreshAddLineItemCosting();
    }, VENUE_DETAILS_COSTING_DEBOUNCE_MS);
  }, [refreshAddLineItemCosting]);

  const handlePricingChange = (next: PricingStrategyValues) => {
    setPricing(next);
  };

  useEffect(() => {
    costingStateRef.current = {
      formData: lineItemsPageResponse,
      eventType,
      customerName: pageHeader?.customerName,
      pricing,
      bookedAddOns,
      hourlyPricing,
      pricePackages: pricePackageList,
    };
  }, [
    lineItemsPageResponse,
    eventType,
    pageHeader?.customerName,
    pricing,
    bookedAddOns,
    hourlyPricing,
    pricePackageList,
  ]);

  useEffect(() => {
    if (!openDrawer) {
      savingRef.current = false;
      setIsSaving(false);
      if (costingDebounceTimerRef.current) {
        clearTimeout(costingDebounceTimerRef.current);
        costingDebounceTimerRef.current = null;
      }
      costingRequestSeqRef.current += 1;
      setCostingPageResponse({});
      setCostingError(null);
      setCostingLoading(false);
      return;
    }
    scheduleAddLineItemCostingRefresh();
  }, [
    openDrawer,
    lineItemsPageResponse,
    pricing,
    bookedAddOns,
    hourlyPricing,
    pricePackageList,
    eventType,
    pageHeader?.customerName,
    scheduleAddLineItemCostingRefresh,
  ]);

  useEffect(
    () => () => {
      if (costingDebounceTimerRef.current) {
        clearTimeout(costingDebounceTimerRef.current);
      }
    },
    [],
  );

  const handleClose = () => {
    pendingLineItemRequestRef.current = null;
    lineItemCreatedRef.current = false;
    setPendingLineItemStatus('');
    setOpenDrawer(false);
    setHasError(false);
    setCostingPageResponse({});
    setCostingError(null);
    setCostingLoading(false);
    setLineItemsPageResponse({
      ...dates,
      partOfDay: '',
      partOfDayUserOverride: false,
    });
    setBookedAddOns([]);
    setAddonsList([]);
    setPricing({
      overridePricePerPerson: false,
      pricePerPerson: null,
      overrideMinimumRevenue: false,
      minimumRevenue: null,
      overrideHallRental: false,
      hallRental: null,
      overridePricePerHour: false,
      pricePerHour: null,
    });
  };
  const handleCatch = (error: any) => {
    setIsLoading(false);
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description
          ? error.response.data.error_description
          : error.response.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const loadMasterData = async () => {
    if (masterLoadedRef.current) return;
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getDataForNewBooking);
      const venues = createDropdownData(data?.venues, ['id', 'name']);
      const capacities = data?.venueEventCapacities ?? [];
      setBaseVenueList(venues);
      setVenueEventCapacities(capacities);
      setVenueList(buildVenuesForEventType(venues, capacities, eventType?.value ?? eventType));
      setPartOfDays(createDropdownData(data?.partOfDays, ['id', 'name']));
      setEventSubtypeOptions(createDropdownData(data?.eventSubTypes, ['name', 'name']));
      setPastBookingWindowDays(data?.pastBookingWindowDays ?? null);
      masterLoadedRef.current = true;
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!openDrawer) return;
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer]);

  const openDrawerAndReset = () => {
    pendingLineItemRequestRef.current = null;
    lineItemCreatedRef.current = false;
    setPendingLineItemStatus('');
    lineItemIdempotencyKeyRef.current =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    setLineItemsPageResponse({
      ...dates,
      partOfDay: '',
      partOfDayUserOverride: false,
    });
    setBookedAddOns([]);
    setOpenDrawer(true);
  };

  const getDrodowns = async (selectedEventType: any, selectedVenue: any) => {
    try {
      if (selectedEventType !== '' && selectedVenue !== '') {
        closeNotification(false);
        setIsLoading(true);
        const { data } = await axiosInstance.get(
          URLS.getDropdownsForVenueConfig(selectedEventType, selectedVenue, 'false'),
        );
        setAddonsList(data?.addOns);
        setHourlyPricing(data?.hourlyPricing);
        setStatusList(createDropdownData(status(strings), ['id', 'name']));
        setPricePackageList(
          data?.pricePackages?.map((p: any) => ({
            label: p.name,
            value: p.id,
            pricePerPerson: p.pricePerPerson,
          })) ?? [],
        );
        setLayoutList(createLayoutDropdownData(data?.layouts) ?? []);
        setTaxGroupList(createDropdownData(data?.taxAndScDetails?.taxGroups, ['id', 'name']) ?? []);
        setDefaultMinimumRevenue(data?.minimumRevenue);
        setDefaultPricePerHour(data?.pricePerHour);
        setDefaultHallRental(data?.fixedHallRental);
        setDefaultPricePerPerson(
          data?.pricePackages?.find((p: any) => p.id === lineItemsPageResponse.pricePackage)?.pricePerPerson,
        );
        setIsOdc(data.isOdc);
        setVenueMaxCapacity(data?.maximumBookCapacity ?? null);

        const startParsed = parseTimeValue(lineItemsPageResponse.startTime as string);
        const endParsed = parseTimeValue(lineItemsPageResponse.endTime as string);
        const diff =
          startParsed && endParsed
            ? endParsed.diff(startParsed, 'hours', true).toFixed(1)
            : '0';
        const obj = {
          ...lineItemsPageResponse,
          venue: selectedVenue,
          postLeadTime: data?.postLeadTime,
          preLeadTime: data?.preLeadTime,
          pricePerHour: data?.pricePerHour,
          totalRental: (diff as any) * data?.pricePerHour,
          taxGroupOnGuestCharge: data?.taxAndScDetails?.selectedTaxGroupOnGuestCharge?.value ?? '',
          taxGroupOnHallRental: data?.taxAndScDetails?.selectedTaxGroupOnHallRental?.value ?? '',
          serviceChargePercentage: data?.taxAndScDetails.serviceChargePercentage,
          excessGuestsPercentage: Number(data?.excessGuestsPercentage) || 0,
        };
        setLineItemsPageResponse(obj);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLineItemFormChange = (field: string, value: string | boolean, formData: FormDataModel) => {
    if (field === 'pricePackage') {
      if (formData.venue === '') {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.new_booking_venue_error,
        });
      }
    }
  };

  const handleLineItemChange = (field: string, value: any, formData: FormDataModel) => {
    if (field === 'partOfDay') {
      setLineItemsPageResponse((prev: any) => ({ ...prev, partOfDayUserOverride: true, partOfDay: value }));
      return;
    }
    if (field === 'startDate') {
      if (dayjs(value).isValid()) {
        lineItemFormRef.current?.handleChange(value, 'endDate', { error: false, message: '' });
        let obj = { ...lineItemsPageResponse };
        obj['startDate'] = value;
        obj['endDate'] = value;
        setLineItemsPageResponse(obj);
      }
    } else if (field === 'startTime' || field === 'endTime') {
      if (hourlyPricing) {
        if (value === '') {
          setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
        } else {
          const nextStart = field === 'startTime' ? value : formData.startTime;
          const nextEnd = field === 'endTime' ? value : formData.endTime;
          const rate = getEffectivePricePerHour(pricing, defaultPricePerHour, lineItemsPageResponse?.pricePerHour);
          if (nextStart && nextEnd && rate) {
            const start = parseTimeValue(nextStart as string);
            const end = parseTimeValue(nextEnd as string);
            const diff = start && end ? end.diff(start, 'hours', true) : NaN;
            if (!Number.isFinite(diff) || diff <= 0) {
              setMessage({
                display: true,
                severity: 'error',
                message: strings.timePickerValidation,
              });
              setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
            } else {
              setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
            }
          } else {
            setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
          }
        }
      } else {
        setLineItemsPageResponse((prev: any) => ({ ...prev, [field]: value }));
      }
      if (field === 'startTime' && value) {
        const userOverrode = (lineItemsPageResponse as any)?.partOfDayUserOverride;
        if (!userOverrode) {
          const part = determinePartOfDay(formatTimeForApi(value as string));
          if (part) {
            (lineItemFormRef.current as any)?.handleChange?.(part, 'partOfDay', { error: false, message: '' });
            setLineItemsPageResponse((prev: any) => ({ ...prev, partOfDay: part }));
          }
        }
      }
    } else if (field === 'venue') {
      lineItemsPageResponse.pricePackage = '';
      lineItemsPageResponse.customPricePackageName = '';
      lineItemsPageResponse.layoutObj = '';
      lineItemsPageResponse.serviceChargePercentage = '';
      lineItemsPageResponse.serviceCharge = null;
      lineItemsPageResponse.taxGroupOnGuestCharge = '';
      lineItemsPageResponse.taxGroupOnHallRental = '';
      lineItemsPageResponse.layout = '';
      setTaxGroupList([]);
      setServiceChargeList([]);
      setBookedAddOns([]);
      setLayoutList([]);
      setPricePackageList([]);
      setAddonsList([]);
      getDrodowns(eventType, value);
    } else if (field === 'pricePackage') {
      const defaultPrice = (pricePackageList ?? []).find((p: any) => p.value === value)?.pricePerPerson;
      setDefaultPricePerPerson(defaultPrice);
      let obj = { ...lineItemsPageResponse };
      obj[field] = value;
      obj.customPricePackageName = isCustomizePackage(value, pricePackageList ?? [])
        ? obj.customPricePackageName
        : '';
      setLineItemsPageResponse(obj);
    } else {
      let obj = { ...lineItemsPageResponse };
      obj[field] = value;
      setLineItemsPageResponse(obj);
    }
  };
  const handleCloseSendEmailDrawer = () => {
    setOpenSendEmailDrawer(false);
    if (lineItemCreatedRef.current) {
      handleClose();
      onSuccess();
      return;
    }
    setOpenDrawer(true);
  };

  const postNewLineItem = async (req: Record<string, any>) => {
    if (lineItemCreatedRef.current) return;
    if (!lineItemIdempotencyKeyRef.current) {
      lineItemIdempotencyKeyRef.current =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
    }
    await axiosInstance.post(URLS.addBookingLineItem(bookingId), req, {
      headers: { 'X-Request-Id': lineItemIdempotencyKeyRef.current },
    });
    lineItemCreatedRef.current = true;
  };

  const handleSaveWithSendEmail = async (e: any) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    try {
      const { getFormData } = sendEmailFormRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };

      const { formData, isFormValid } = getFormData();
      setSendEmailHasError(false);

      if (isFormValid) {
        const pendingRequest = pendingLineItemRequestRef.current;
        if (!pendingRequest) {
          setOutsideMessage({
            display: true,
            severity: 'error',
            message: strings.lineItemError,
          });
          return;
        }
        const emailFormData = removeErrorFieldsFromValues(formData);
        setIsLoading(true);
        await postNewLineItem(pendingRequest);

        const emailRequest = new FormData();
        emailRequest.append('deletedFiles', deletedImage);
        emailRequest.append('to', emailFormData.to);
        emailRequest.append('cc', emailFormData.cc);
        emailRequest.append('bcc', emailFormData.bcc);
        emailRequest.append('type', 'EDIT_BOOKING');
        attachment.forEach((file: File) => {
          emailRequest.append('files', file);
        });
        appendEmailAttachmentOptions(emailRequest, emailAttachmentVisibility);

        const requestHeaders = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
        await axiosInstance.post(URLS.sendBookingEmailAttachment(bookingId), emailRequest, requestHeaders);
        setOutsideMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
        setOpenSendEmailDrawer(false);
        setTimeout(() => {
          handleClose();
          onSuccess();
        }, 1000);
        return true;
      } else {
        setOutsideMessage({
          display: true,
          severity: 'error',
          message: `${strings.required_error_prefix_message} ${fetchErrorFields(
            formData,
            NewBookingSendEmailFormComponent(strings),
          ).join(', ')}`,
        });
        setSendEmailHasError(true);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };
  const isTimeMissing = (time: unknown) => parseTimeValue(time as string) == null;

  const confirmCostingBeforeSave = async (formData: Record<string, any>): Promise<boolean> => {
    if (costingDebounceTimerRef.current) {
      clearTimeout(costingDebounceTimerRef.current);
      costingDebounceTimerRef.current = null;
    }
    const requestSeq = ++costingRequestSeqRef.current;
    const requestBody = buildAddLineItemCostingRequest({
      formData,
      eventType,
      customerName: pageHeader?.customerName,
      pricing,
      bookedAddOns,
      hourlyPricing,
      pricePackages: pricePackageList ?? [],
    });
    if (!requestBody) {
      const message = strings.new_booking_event_summary_error;
      setCostingPageResponse({});
      setCostingError(message);
      setMessage({
        display: true,
        severity: 'error',
        message,
      });
      return false;
    }
    setCostingLoading(true);
    try {
      const { status, data: responseData } = await axiosInstance.post(URLS.calculateBill, requestBody);
      if (requestSeq !== costingRequestSeqRef.current) return false;
      if (status === 200 || status === 201) {
        setCostingPageResponse(responseData ?? {});
        setCostingError(null);
        return true;
      }
      const message = strings.new_booking_event_summary_error;
      setCostingPageResponse({});
      setCostingError(message);
      setMessage({
        display: true,
        severity: 'error',
        message,
      });
      return false;
    } catch (error: any) {
      if (requestSeq !== costingRequestSeqRef.current) return false;
      const message = extractCostingErrorMessage(error, strings.new_booking_event_summary_error);
      setCostingPageResponse({});
      setCostingError(message);
      setMessage({
        display: true,
        severity: 'error',
        message,
      });
      return false;
    } finally {
      if (requestSeq === costingRequestSeqRef.current) {
        setCostingLoading(false);
      }
    }
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    if (savingRef.current || isLoading || isSaving) return;
    savingRef.current = true;
    setIsSaving(true);

    const part1 = lineItemFormRef.current as
      | {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
          handleChange: (value: string | boolean, field: string, error?: { error: boolean; message: string }) => void;
        }
      | null
      | undefined;
    const part2 = lineItemTaxFormRef.current as
      | { getFormData: () => { formData: FormDataModel; isFormValid: boolean } }
      | null
      | undefined;
    if (!part1) {
      savingRef.current = false;
      setIsSaving(false);
      return;
    }
    const { formData: data1, isFormValid: valid1 } = part1.getFormData();
    const { formData: data2, isFormValid: valid2 } = part2
      ? part2.getFormData()
      : { formData: {} as FormDataModel, isFormValid: true };
    let formData: FormDataModel = { ...data1, ...data2 };
    let isFormValid = valid1 && valid2;
    let flag = 0;
    if (!hourlyPricing && formData.pricePackage === '') {
      formData.pricePackageError = true;
      flag = 1;
    }
    if (isTimeMissing(data1.startTime)) {
      part1.handleChange('', 'startTime', { error: true, message: strings.requiredText });
      flag = 1;
    }
    if (isTimeMissing(data1.endTime)) {
      part1.handleChange('', 'endTime', { error: true, message: strings.requiredText });
      flag = 1;
    }
    if (flag) isFormValid = false;
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      if (data.startDate === 'Invalid Date' || data.endDate === 'Invalid Date') {
        savingRef.current = false;
        setIsSaving(false);
        return;
      }
      const newStartDate = dayjs(data.startDate as string);
      const newEndDate = dayjs(data.endDate as string);
      let bookedAddOnsMaxCountError = [] as any;
      let bookedAddOnsNumberError = [] as any;
      let bookedAddOnsPriceError = [] as any;
      bookedAddOns.forEach((item: any) => {
        if (item.type === 'PER_QUANTITY' && !item.quantity) item = { ...item, quantity: 0 };

        if (item.type === 'PER_QUANTITY' && item.quantity < 1) {
          bookedAddOnsNumberError.push(item.name);
        } else if (item.type === 'PER_QUANTITY' && item.quantity > item.maxCount) {
          bookedAddOnsMaxCountError.push(item.name);
        } else if (item.price < 0) {
          bookedAddOnsPriceError.push(item.name);
        }
      });
      if (newEndDate.isBefore(newStartDate)) {
        savingRef.current = false;
        setIsSaving(false);
        setMessage({
          display: true,
          severity: 'error',
          message: strings.datePickerValidation,
        });
      } else if (
        (() => {
          const start = parseTimeValue(data.startTime as string);
          const end = parseTimeValue(data.endTime as string);
          return start && end && (start.isSame(end) || start.isAfter(end));
        })()
      ) {
        savingRef.current = false;
        setIsSaving(false);
        setMessage({
          display: true,
          severity: 'error',
          message: strings.timePickerValidation,
        });
      } else if (bookedAddOnsMaxCountError.length > 0) {
        savingRef.current = false;
        setIsSaving(false);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsMaxCountError.join(', ')} ${strings.addOn_MaxCountError}`,
        });
      } else if (bookedAddOnsNumberError.length > 0) {
        savingRef.current = false;
        setIsSaving(false);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsNumberError.join(', ')} ${strings.addOn_Error}`,
        });
      } else if (bookedAddOnsPriceError.length > 0) {
        savingRef.current = false;
        setIsSaving(false);
        setMessage({
          display: true,
          severity: 'error',
          message: `${bookedAddOnsPriceError.join(', ')} ${strings.addOnPriceError}`,
        });
      } else {
        setIsLoading(true);
        try {
          // Pricing override fields come from the PricingStrategySection state
          // (`pricing`). Send only the values the user explicitly overrode –
          // a null value tells the backend to fall back to the venue default.
          const newData = {
            ...data,
            eventSubtype:
              typeof data.eventSubtype === 'object'
                ? (data.eventSubtype as any)?.label || (data.eventSubtype as any)?.value || ''
                : (data.eventSubtype as any),
            startDate: dayjs(data.startDate).format('DD-MM-YYYY'),
            endDate: dayjs(data.endDate).format('DD-MM-YYYY'),
            startTime: formatTimeForApi(data.startTime as string),
            endTime: formatTimeForApi(data.endTime as string),
            pricePackageId: hourlyPricing ? null : data.pricePackage,
            customPricePackageName:
              !hourlyPricing && isCustomizePackage(data.pricePackage, pricePackageList ?? [])
                ? normalizeCustomPricePackageName(data.customPricePackageName)
                : null,
            pricePerHour:
              hourlyPricing && pricing.overridePricePerHour ? pricing.pricePerHour : null,
            addOns: bookedAddOns,
            layout: data.layout === '' ? null : data.layout,
            serviceChargePercentage:
              data.serviceCharge === undefined
                ? data.serviceChargePercentage
                : data.serviceCharge?.hasOwnProperty?.('value')
                  ? data.serviceCharge.value
                  : data.serviceCharge,
            taxGroupOnGuestChargeId:
              (data.taxGroupOnGuestCharge as any)?.value ?? data.taxGroupOnGuestCharge ?? null,
            taxGroupOnHallRentalId:
              (data.taxGroupOnHallRental as any)?.value ?? data.taxGroupOnHallRental ?? null,
            pricePerPerson: !hourlyPricing && pricing.overridePricePerPerson ? pricing.pricePerPerson : null,
            minimumRevenue: !hourlyPricing && pricing.overrideMinimumRevenue ? pricing.minimumRevenue : null,
            hallRental: !hourlyPricing && pricing.overrideHallRental ? pricing.hallRental : null,
          };
          const { s1, s2, ...req } = newData as any;
          const costingSucceeded = await confirmCostingBeforeSave(data);
          if (!costingSucceeded) return;
          pendingLineItemRequestRef.current = req;
          lineItemCreatedRef.current = false;
          setPendingLineItemStatus(String(data.status || ''));
          const arr =
            bookingEmail === '' ? [loggedInUserDetails.email] : [loggedInUserDetails.email, bookingEmail];
          setSendEmailPageResponse({ to: arr.join(',') });
          setOpenDrawer(false);
          setOpenSendEmailDrawer(true);
        } catch (e: any) {
          handleCatch(e);
        } finally {
          setIsLoading(false);
          savingRef.current = false;
          setIsSaving(false);
        }
      }
    } else {
      savingRef.current = false;
      setIsSaving(false);
      flushSync(() => setHasError(true));
      setTimeout(() => {
        const element = document.querySelector('.Mui-error');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  };
  const selectedPackageLabel =
    (pricePackageList ?? []).find((p: any) => String(p.value) === String(lineItemsPageResponse?.pricePackage))
      ?.label ?? null;
  const selectedVenueLabel =
    (venueList ?? []).find((v: any) => String(v.value) === String(lineItemsPageResponse?.venue))?.label ?? null;
  const selectedVenueMaxCapacity =
    venueMaxCapacity ??
    (venueList ?? []).find((v: any) => String(v.value) === String(lineItemsPageResponse?.venue))?.maxCapacity ??
    null;
  const selectedLayoutMaxCapacity =
    (layoutList ?? []).find((l: any) => String(l.value) === String(lineItemsPageResponse?.layout))?.maxCapacity ??
    null;
  const selectedLayoutLabel =
    (layoutList ?? []).find((l: any) => String(l.value) === String(lineItemsPageResponse?.layout))?.label ?? null;

  const headerMetaItems: { icon: ReactNode; label: string }[] = [];
  if (pageHeader?.customerName?.trim()) {
    headerMetaItems.push({
      icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
      label: pageHeader.customerName.trim(),
    });
  }
  if (pageHeader?.eventTypeLabel?.trim()) {
    headerMetaItems.push({
      icon: <EventOutlinedIcon sx={{ fontSize: 15 }} />,
      label: pageHeader.eventTypeLabel.trim(),
    });
  }
  if (selectedVenueLabel?.trim()) {
    headerMetaItems.push({
      icon: <PlaceOutlinedIcon sx={{ fontSize: 15 }} />,
      label: selectedVenueLabel.trim(),
    });
  }

  const drawerTitle = (
    <Box sx={{ minWidth: 0, pr: 1 }}>
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={{
          mb: headerMetaItems.length ? 0.35 : 0,
          '& .MuiBreadcrumbs-separator': { mx: 0.75, color: 'inherit', opacity: 0.7 },
          '& .MuiBreadcrumbs-li': { color: 'inherit' },
        }}>
        <Typography
          component="span"
          sx={{ fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.35, color: 'inherit', opacity: 0.85 }}>
          {strings.new_booking_breadcrumb_booking}
        </Typography>
        <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.35, color: 'inherit' }}>
          {strings.new_booking_breadcrumb_events_venues}
        </Typography>
      </Breadcrumbs>
      {headerMetaItems.length ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={0.75}
          sx={{ rowGap: 0.25, color: 'inherit', opacity: 0.9 }}>
          {headerMetaItems.map((item, index) => (
            <Stack
              key={`${item.label}-${index}`}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0 }}>
              {index > 0 ? (
                <Typography
                  component="span"
                  sx={{ fontSize: '0.8rem', lineHeight: 1, mr: 0.25, opacity: 0.7 }}
                  aria-hidden>
                  ·
                </Typography>
              ) : null}
              <Box sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, opacity: 0.9 }}>{item.icon}</Box>
              <Typography
                noWrap
                sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.35, color: 'inherit' }}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Box>
  );

  return (
    <>
      {isLoading && <PageLoader />}
      {outsideMessage.display && (
        <Notification
          isOpen={outsideMessage.display}
          message={outsideMessage.message}
          severity={outsideMessage.severity as AlertColor}
          closeNotification={closeOutsideNotification}
        />
      )}
      <Button
        size="small"
        onClick={openDrawerAndReset}
        variant="contained"
        sx={{ ...bookingDetailToolbarButtonSx }}
        color="primary"
        startIcon={<AddCircleOutline />}>
        {strings.addNewLineItem}
      </Button>
      <DrawerMenu
        width={getLineItemDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton
        dropBack={false}
        overlapHeader
        flexColumn
        noPadding
        title={drawerTitle}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            px: { xs: 1.25, md: 1.75 },
            pt: 1,
            pb: 0,
          }}>
          {message.display && (
            <Notification
              isOpen={message.display}
              message={message.message}
              severity={message.severity as AlertColor}
              closeNotification={closeNotification}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1, md: 1.25 },
              minHeight: 0,
              overflow: { xs: 'auto', md: 'hidden' },
            }}>
            <Box
              sx={{
                width: { xs: '100%', md: '74%' },
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowY: 'auto',
                pb: 1,
              }}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  pt: { xs: 1, md: 1.25 },
                  px: { xs: 1.25, md: 1.75 },
                  pb: { xs: 1.25, md: 1.75 },
                }}>
                <Grid container spacing={1.5} sx={{ mt: 0, mb: 0.5 }} alignItems="flex-start">
                  <Form
                    dropdownData={{ layout: layoutList ?? [] }}
                    hasError={hasError}
                    ref={lineItemFormRef as RefObject<Form>}
                    model={lineItemsForm({
                      strings,
                      venues: venueList ?? [],
                      statuses: statusList ?? [],
                      layouts: layoutList ?? [],
                      pricepackages: pricePackageList ?? [],
                      isDisabled: false,
                      serviceChargeDropdown,
                      serviceChargeList,
                      // Allow any date within the org booking window (not locked to the current line item).
                      selectedStartDate: null,
                      selectedEndDate: null,
                      hourlyPricing,
                      isOdc,
                      partOfDays,
                      eventSubtypeOptions,
                      excessGuestsPercentage: lineItemsPageResponse?.excessGuestsPercentage,
                      selectedPricePackage: lineItemsPageResponse?.pricePackage,
                      pastBookingWindowDays,
                      layoutMaxCapacity: selectedLayoutMaxCapacity,
                      layoutName: selectedLayoutLabel,
                      layoutSelected: Boolean(lineItemsPageResponse?.layout),
                      venueMaxCapacity: selectedVenueMaxCapacity,
                      venueName: selectedVenueLabel,
                      venueSelected: Boolean(lineItemsPageResponse?.venue),
                    })}
                    values={lineItemsPageResponse}
                    onFocus={handleLineItemFormChange}
                    onChange={handleLineItemChange}
                  />
                </Grid>
                <Grid container spacing={1.5} sx={{ mt: 0, mb: 0.5 }}>
                  <PricingStrategySection
                    values={pricing}
                    defaults={{
                      pricePerPerson: defaultPricePerPerson,
                      minimumRevenue: defaultMinimumRevenue,
                      pricePerHour: defaultPricePerHour ?? lineItemsPageResponse?.pricePerHour,
                      hallRental: hourlyPricing ? hourlyTotalRental : defaultHallRental,
                      hourlyRentalBreakdown: hourlyPricingBreakdown,
                      packageName: selectedPackageLabel,
                      venueName: selectedVenueLabel,
                    }}
                    onChange={handlePricingChange}
                    hourlyPricing={hourlyPricing}
                  />
                </Grid>
                <Grid container spacing={1.5} sx={{ mt: 0, mb: 0.5 }} alignItems="flex-start">
                  <Form
                    dropdownData={{
                      taxGroupOnGuestCharge: taxGroupList,
                      taxGroupOnHallRental: taxGroupList,
                    }}
                    hasError={hasError}
                    ref={lineItemTaxFormRef as RefObject<Form>}
                    model={lineItemsTaxForm({
                      strings,
                      taxGroups: taxGroupList ?? [],
                      venueSelected: Boolean(lineItemsPageResponse?.venue),
                    })}
                    values={lineItemsPageResponse}
                    onChange={handleLineItemChange}
                  />
                </Grid>
                {addonsList && Object.keys(addonsList).length > 0 && (
                  <NewBookingAddon
                    addonsDropdown={[
                      ...Object.keys(addonsList)
                        .map(key => addonsList[key])
                        .flat(),
                    ]}
                    bookedAddOns={bookedAddOns}
                    setBookedAddOns={setBookedAddOns}
                  />
                )}
              </Paper>
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: '26%' },
                flexShrink: 0,
                position: { md: 'sticky' },
                top: { md: 0 },
                alignSelf: { md: 'flex-start' },
                maxHeight: { md: '100%' },
                overflowY: { md: 'auto' },
              }}>
              <VenueDetailsPageSummary
                pageResponse={costingPageResponse}
                loading={costingLoading}
                error={costingError}
              />
            </Box>
          </Box>
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              px: { xs: 1, md: 1.25 },
              py: 0.75,
              my: 1,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.75,
            }}>
            <Button variant="outlined" size="small" onClick={handleClose} disabled={isLoading || isSaving}>
              {strings.receivable_button_cancel}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ color: '#ffffff' }}
              disabled={isLoading || isSaving}
              onClick={e => handleSave(e)}>
              {strings.common_button_save}
            </Button>
          </Paper>
        </Box>
      </DrawerMenu>
      <NewBookingSendEmailComponent
        submitButtonText={strings.common_button_save}
        openDrawer={openSendEmailDrawer}
        formRef={sendEmailFormRef}
        handleCloseDrawer={handleCloseSendEmailDrawer}
        hasError={sendEmailHasError}
        pageResponse={sendEmailPageResponse}
        handleSave={(e: any) => handleSaveWithSendEmail(e)}
        attachment={attachment}
        deletedImage={deletedImage}
        getFileDetails={getFileDetails}
        disabled={isLoading}
        {...emailAttachmentVisibility}
      />
    </>
  );
};

export default AddLineItem;
